import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, CheckCircle, AlertTriangle, XCircle, Clock,
  Wrench, Plus, ClipboardList, MessageSquare, BarChart2, ArrowLeft,
  Gauge, Fuel, CalendarCheck, ChevronRight, Loader2
} from 'lucide-react';
import api from '../api/api';

const STATUS_MAP = {
  operative:    { label: 'Operativo',    color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', icon: CheckCircle },
  breakdown:    { label: 'Avería',       color: 'bg-red-500',     text: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-400',     icon: XCircle },
  maintenance:  { label: 'Mantenimiento',color: 'bg-amber-500',   text: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-400',   icon: Wrench },
  inactive:     { label: 'Inactivo',     color: 'bg-gray-400',    text: 'text-gray-500',    bg: 'bg-gray-50',    border: 'border-gray-300',    icon: Clock },
};

const PLAN_STATUS = {
  critical: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', label: 'Vencido' },
  warning:  { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Próximo' },
  ok:       { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', label: 'OK' },
};

const PRIORITY_COLORS = {
  low:      'bg-gray-100 text-gray-700',
  medium:   'bg-blue-100 text-blue-700',
  high:     'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
};
const PRIORITY_LABELS = { low:'Baja', medium:'Media', high:'Alta', critical:'Crítica' };

function Section({ title, icon: Icon, count, children, defaultOpen = false, accentColor = 'border-[#16a34a]' }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`bg-white rounded-2xl shadow-sm border-l-4 ${accentColor} overflow-hidden`}>
      <button
        className="w-full flex items-center justify-between px-4 py-4 text-left active:bg-gray-50"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2.5">
          <Icon size={20} className="text-[#16a34a] flex-shrink-0" />
          <span className="font-semibold text-gray-900 text-base">{title}</span>
          {count !== undefined && (
            <span className="ml-1 text-xs font-bold bg-[#16a34a] text-white rounded-full px-2 py-0.5">{count}</span>
          )}
        </div>
        {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default function TallerView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [maintenancePlan, setMaintenancePlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [toast, setToast] = useState(null);

  // Forms state
  const [statusVal, setStatusVal] = useState('');
  const [horForm, setHorForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), hours: '', diesel: '', notes: '' });
  const [otForm, setOtForm] = useState({ type: 'corrective', priority: 'high', title: '', description: '' });
  const [obsText, setObsText] = useState('');

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    try {
      const [eqRes, woRes, planRes] = await Promise.all([
        api.get(`/equipment/${id}`),
        api.get(`/work-orders?equipment_id=${id}`),
        api.get(`/equipment/${id}/maintenance-plan`),
      ]);
      setEquipment(eqRes.data);
      setStatusVal(eqRes.data.status || 'operative');
      setWorkOrders(woRes.data.filter(w => ['pending', 'in_progress'].includes(w.status)));
      setMaintenancePlan(planRes.data);
    } catch {
      showToast('Error cargando datos del equipo', false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === statusVal) return;
    setSaving('status');
    try {
      await api.put(`/equipment/${id}`, { status: newStatus });
      setStatusVal(newStatus);
      setEquipment(eq => ({ ...eq, status: newStatus }));
      showToast(`Estado actualizado: ${STATUS_MAP[newStatus]?.label}`);
    } catch {
      showToast('Error actualizando estado', false);
    } finally { setSaving(''); }
  };

  const handleHorometro = async (e) => {
    e.preventDefault();
    if (!horForm.hours) return;
    setSaving('hor');
    const year = parseInt(horForm.year);
    const month = parseInt(horForm.month);
    const prev = await api.get(`/hourometer?equipment_id=${id}&year=${year}`).then(r => r.data).catch(() => []);
    const prevMonth = prev.filter(r => r.month < month);
    const prevHoursSum = prevMonth.reduce((s, r) => s + (r.hours || 0), 0);
    const hoursTotal = prevHoursSum + parseFloat(horForm.hours || 0);
    try {
      await api.post('/hourometer', {
        equipment_id: id,
        year,
        month,
        hours: parseFloat(horForm.hours || 0),
        hours_total_machine: hoursTotal,
        km: 0,
        diesel: parseFloat(horForm.diesel || 0),
        hydraulic_oil: 0,
        notes: horForm.notes,
      });
      showToast('Horómetro registrado correctamente');
      setHorForm(f => ({ ...f, hours: '', diesel: '', notes: '' }));
      load();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Error registrando horómetro', false);
    } finally { setSaving(''); }
  };

  const handleCreateOT = async (e) => {
    e.preventDefault();
    if (!otForm.title.trim()) return;
    setSaving('ot');
    try {
      await api.post('/work-orders', {
        equipment_id: id,
        equipment_name: equipment?.name || '',
        type: otForm.type,
        priority: otForm.priority,
        status: 'pending',
        title: otForm.title,
        description: otForm.description,
        assigned_to: '',
        estimated_hours: 0,
      });
      showToast('Orden de trabajo creada');
      setOtForm({ type: 'corrective', priority: 'high', title: '', description: '' });
      load();
    } catch {
      showToast('Error creando la OT', false);
    } finally { setSaving(''); }
  };

  const handleObservacion = async () => {
    if (!obsText.trim()) return;
    setSaving('obs');
    try {
      await api.post(`/equipment/${id}/observation`, { text: obsText });
      showToast('Observación registrada');
      setObsText('');
    } catch {
      showToast('Error guardando observación', false);
    } finally { setSaving(''); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="animate-spin w-10 h-10 text-[#16a34a] mx-auto mb-3" />
        <p className="text-sm text-gray-500">Cargando equipo...</p>
      </div>
    </div>
  );

  if (!equipment) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-gray-700 font-medium">Equipo no encontrado</p>
        <button onClick={() => navigate('/')} className="mt-4 text-sm text-[#16a34a] underline">Ir al inicio</button>
      </div>
    </div>
  );

  const st = STATUS_MAP[equipment.status] || STATUS_MAP.operative;
  const StIcon = st.icon;

  const pendingOTs = workOrders.filter(w => w.status === 'pending');
  const inProgressOTs = workOrders.filter(w => w.status === 'in_progress');
  const criticalPlan = maintenancePlan.filter(p => p.status === 'critical').length;

  const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

  return (
    <div className="min-h-screen bg-gray-100 pb-8" style={{ fontFamily: "'Barlow', 'Inter', sans-serif" }}>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.ok ? 'bg-emerald-600' : 'bg-red-500'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-[#111827] text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-start gap-3">
          <button onClick={() => navigate(-1)} className="mt-0.5 p-1.5 rounded-lg hover:bg-white/10 active:bg-white/20">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${st.color} text-white`}>
                <StIcon size={11} />
                {st.label}
              </span>
              {criticalPlan > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">
                  <AlertTriangle size={11} /> {criticalPlan} mantenim. vencido{criticalPlan > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <h1 className="font-bold text-lg leading-tight mt-0.5 truncate">{equipment.name}</h1>
            <p className="text-xs text-gray-400">{equipment.code} · {equipment.type === 'mobile' ? 'Maq. Móvil' : 'Maq. Fija'} · <span className="text-emerald-400 font-semibold">{(equipment.total_hours || 0).toFixed(0)} h totales</span></p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 pt-4 space-y-3">

        {/* ESTADO RÁPIDO */}
        <Section title="Cambiar Estado" icon={CheckCircle} defaultOpen={true} accentColor="border-[#16a34a]">
          <div className="grid grid-cols-2 gap-2.5 mt-1">
            {Object.entries(STATUS_MAP).map(([key, val]) => {
              const Ic = val.icon;
              const isActive = statusVal === key;
              return (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  disabled={saving === 'status'}
                  data-testid={`status-btn-${key}`}
                  className={`flex items-center gap-2.5 px-3 py-3.5 rounded-xl border-2 transition-all active:scale-95 ${
                    isActive
                      ? `${val.border} ${val.bg} font-bold`
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full ${val.color} flex items-center justify-center flex-shrink-0`}>
                    <Ic size={16} className="text-white" />
                  </div>
                  <span className={`text-sm font-semibold ${isActive ? val.text : 'text-gray-700'}`}>{val.label}</span>
                  {isActive && <CheckCircle size={14} className={`ml-auto ${val.text}`} />}
                </button>
              );
            })}
          </div>
          {saving === 'status' && <p className="text-xs text-center text-gray-400 mt-2">Actualizando...</p>}
        </Section>

        {/* REGISTRAR HORÓMETRO */}
        <Section title="Registrar Horómetro" icon={Gauge} accentColor="border-blue-500">
          <form onSubmit={handleHorometro} className="space-y-3 mt-1">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Mes</label>
                <select
                  value={horForm.month}
                  onChange={e => setHorForm(f => ({ ...f, month: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="hor-month"
                >
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Año</label>
                <input
                  type="number" value={horForm.year}
                  onChange={e => setHorForm(f => ({ ...f, year: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="hor-year"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Horas trabajadas este mes *</label>
              <div className="relative">
                <input
                  type="number" placeholder="ej. 145" value={horForm.hours} required
                  onChange={e => setHorForm(f => ({ ...f, hours: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3.5 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="hor-hours"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">horas</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Combustible consumido</label>
              <div className="relative">
                <Fuel size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number" placeholder="0" value={horForm.diesel}
                  onChange={e => setHorForm(f => ({ ...f, diesel: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl pl-8 pr-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="hor-diesel"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">litros</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Observaciones</label>
              <textarea
                value={horForm.notes} rows={2} placeholder="Incidencias del mes, trabajos realizados..."
                onChange={e => setHorForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="hor-notes"
              />
            </div>
            <button
              type="submit" disabled={saving === 'hor' || !horForm.hours}
              className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="hor-submit"
            >
              {saving === 'hor' ? <Loader2 size={18} className="animate-spin" /> : <Gauge size={18} />}
              {saving === 'hor' ? 'Registrando...' : 'Registrar Horómetro'}
            </button>
          </form>
        </Section>

        {/* NUEVA OT */}
        <Section title="Nueva Orden de Trabajo" icon={Plus} accentColor="border-amber-500">
          <form onSubmit={handleCreateOT} className="space-y-3 mt-1">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Tipo</label>
                <select
                  value={otForm.type} onChange={e => setOtForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="ot-type"
                >
                  <option value="corrective">Correctivo</option>
                  <option value="preventive">Preventivo</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Prioridad</label>
                <select
                  value={otForm.priority} onChange={e => setOtForm(f => ({ ...f, priority: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="ot-priority"
                >
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Título de la OT *</label>
              <input
                type="text" required placeholder="ej. Cambio de filtro de aceite" value={otForm.title}
                onChange={e => setOtForm(f => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="ot-title"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Descripción del trabajo</label>
              <textarea
                rows={3} value={otForm.description} placeholder="Describe el trabajo a realizar..."
                onChange={e => setOtForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="ot-description"
              />
            </div>
            <button
              type="submit" disabled={saving === 'ot' || !otForm.title.trim()}
              className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-base active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="ot-submit"
            >
              {saving === 'ot' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              {saving === 'ot' ? 'Creando OT...' : 'Crear Orden de Trabajo'}
            </button>
          </form>
        </Section>

        {/* OTs PENDIENTES */}
        <Section title="OTs Pendientes" icon={ClipboardList} count={workOrders.length} accentColor="border-purple-500">
          {workOrders.length === 0 ? (
            <div className="py-5 text-center">
              <CheckCircle size={32} className="text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Sin órdenes de trabajo pendientes</p>
            </div>
          ) : (
            <div className="space-y-2.5 mt-1">
              {workOrders.map(wo => (
                <div key={wo.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50" data-testid={`ot-item-${wo.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{wo.title}</p>
                    <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${PRIORITY_COLORS[wo.priority] || 'bg-gray-100 text-gray-600'}`}>
                      {PRIORITY_LABELS[wo.priority] || wo.priority}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}>
                      {wo.status === 'in_progress' ? 'En curso' : 'Pendiente'}
                    </span>
                    <span className="text-xs text-gray-400">{wo.type === 'corrective' ? 'Correctivo' : 'Preventivo'}</span>
                  </div>
                  {wo.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{wo.description}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* PLAN DE MANTENIMIENTO */}
        <Section title="Plan de Mantenimiento" icon={CalendarCheck} count={maintenancePlan.length} accentColor="border-red-400">
          {maintenancePlan.length === 0 ? (
            <p className="text-sm text-gray-400 py-3 text-center">Sin plan de mantenimiento configurado</p>
          ) : (
            <div className="space-y-2.5 mt-1">
              {maintenancePlan.map((item, i) => {
                const ps = PLAN_STATUS[item.status] || PLAN_STATUS.ok;
                const hoursText = item.hours_until_maintenance <= 0
                  ? `${Math.abs(item.hours_until_maintenance).toFixed(0)} h vencido`
                  : `${item.hours_until_maintenance.toFixed(0)} h restantes`;
                return (
                  <div key={i} className={`border ${ps.border} rounded-xl p-3 ${ps.bg}`} data-testid={`plan-item-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ps.badge}`}>{ps.label}</span>
                          <span className="text-xs text-gray-500">{item.interval_hours}h</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{item.maintenance_type}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                      </div>
                      <div className={`text-right flex-shrink-0 ${ps.color}`}>
                        <p className="text-base font-bold">{hoursText}</p>
                        <p className="text-xs text-gray-400">{item.current_hours.toFixed(0)} h actuales</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* OBSERVACIÓN RÁPIDA */}
        <Section title="Añadir Observación" icon={MessageSquare} accentColor="border-gray-400">
          <div className="space-y-3 mt-1">
            <textarea
              rows={3} value={obsText} placeholder="Escribe una observación o nota para este equipo..."
              onChange={e => setObsText(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-400"
              data-testid="obs-textarea"
            />
            <button
              onClick={handleObservacion}
              disabled={saving === 'obs' || !obsText.trim()}
              className="w-full py-4 rounded-xl bg-gray-800 hover:bg-gray-900 text-white font-bold text-base active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="obs-submit"
            >
              {saving === 'obs' ? <Loader2 size={18} className="animate-spin" /> : <MessageSquare size={18} />}
              {saving === 'obs' ? 'Guardando...' : 'Guardar Observación'}
            </button>
          </div>
        </Section>

        {/* Info footer */}
        <div className="text-center py-2">
          <p className="text-xs text-gray-400">GMAO Ecoparque de Cáceres · Valoriza/GESPESA</p>
          <button onClick={() => navigate('/equipos/' + id)} className="text-xs text-[#16a34a] underline mt-0.5">
            Ver ficha completa del equipo <ChevronRight size={11} className="inline" />
          </button>
        </div>
      </div>
    </div>
  );
}
